import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  Archive,
  ArrowRight,
  Building2,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  Database,
  Files,
  FolderOpen,
  HardDriveDownload,
  LayoutDashboard,
  LoaderCircle,
  Search,
  Settings2,
  ShieldCheck,
  UploadCloud,
  Workflow,
} from "lucide-react";
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import type {
  AuthUser,
  CounterProfileRecord,
  DocumentType,
  EntityRole,
  EntitySettingsRecord,
  RequiredDocumentTemplateRecord,
  StocktakeSummary,
  WorkflowTemplate,
} from "../shared/domain";
import { documentTypes } from "../shared/domain";
import { workflowTemplates } from "../shared/workflow";
import { api } from "./lib/api";

type WorkflowStepView = {
  step: StocktakeSummary["steps"][number];
  template: WorkflowTemplate;
  blocked: boolean;
  canEdit: boolean;
};

type SetupTab = "overview" | "documents" | "structure" | "users";

const workflowTemplateByStep = new Map(workflowTemplates.map((template) => [template.stepNo, template]));
const phaseOrder = ["Pre-Stocktake", "Execution", "Reconciliation", "Accountability", "Payroll", "Finalisation"] as const;

function createDocumentDraft(entityId: string, documentType: DocumentType, sortOrder: number): RequiredDocumentTemplateRecord {
  return {
    id: `${entityId}-document-${documentType}-${crypto.randomUUID().slice(0, 8)}`,
    entityId,
    documentType,
    label: documentType.replaceAll("_", " "),
    required: true,
    sortOrder,
    notes: null,
  };
}

function createCounterDraft(entityId: string, sortOrder: number): CounterProfileRecord {
  return {
    id: `${entityId}-counter-${crypto.randomUUID().slice(0, 8)}`,
    entityId,
    name: `Counter ${sortOrder}`,
    roleLabel: sortOrder === 1 ? "Primary Counter" : "Counter Role",
    sortOrder,
    distinctGroup: sortOrder === 1 ? "count-team" : `group-${sortOrder}`,
    notes: null,
  };
}

const masterNav = [
  { to: "/master", label: "Master Overview", icon: LayoutDashboard },
  { to: "/master/entities", label: "Members", icon: Building2 },
  { to: "/master/storage", label: "Storage", icon: Database },
  { to: "/master/backups", label: "Backups", icon: HardDriveDownload },
];

const entityNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/board", label: "Stocktake Board", icon: ClipboardCheck },
  { to: "/reviews", label: "Reviews", icon: ShieldCheck },
  { to: "/payroll", label: "Payroll", icon: Files },
  { to: "/setup", label: "Setup", icon: Settings2 },
];

const entityRoleOptions: Array<{ value: EntityRole; label: string }> = [
  { value: "entity_admin", label: "Member Admin" },
  { value: "resort_ops", label: "Resort Ops" },
  { value: "regional_manager", label: "Regional Manager" },
  { value: "oan", label: "OAN" },
  { value: "payroll", label: "Payroll" },
  { value: "entity_readonly", label: "Member read only" },
];

const oversightCompanyName = "Retail Ops Analysis (ROA)";
const memberWorkspaceLabel = "Member workspace";
const memberDirectoryLabel = "Members / Resorts";
const memberSingularLabel = "Member / Resort";

function App() {
  const authQuery = useQuery<AuthUser | null>({
    queryKey: ["auth", "me"],
    queryFn: api.me,
  });

  if (authQuery.isLoading) {
    return <LoadingScreen label="Loading ROA Stocktake" />;
  }

  return authQuery.data ? <AuthenticatedApp user={authQuery.data} /> : <LoginScreen />;
}

function LoadingScreen({ label }: { label: string }) {
  return (
    <div className="loading-screen">
      <LoaderCircle className="spin" size={26} />
      <span>{label}</span>
    </div>
  );
}

function LoginScreen() {
  const queryClient = useQueryClient();
  const login = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) => api.login(email, password),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["auth", "me"] }),
  });

  return (
    <main className="login-layout">
      <section className="login-panel">
        <div className="login-hero">
          <div className="login-logo-stage">
            <img src="/roa-logo-signature-tight.png" alt="Retail Ops Analysis" className="login-logo" />
          </div>
          <p className="login-panel-summary">
            One ROA oversight console, plus practical member workspaces for resort setup, store control, reviews, and payroll gating.
          </p>
          <div className="login-feature-list" aria-label="Platform highlights">
            <span className="login-feature">Clear ROA and member workspaces</span>
            <span className="login-feature">Structured stocktake progress tracking</span>
            <span className="login-feature">Storage and backup oversight</span>
          </div>
        </div>
      </section>
      <section className="card login-card">
        <div className="login-card-head">
          <p className="eyebrow">Secure access</p>
          <h2>Sign in</h2>
          <p className="muted">Use your ROA or member login to continue.</p>
        </div>
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            const formData = new FormData(event.currentTarget);
            login.mutate({
              email: String(formData.get("email") ?? ""),
              password: String(formData.get("password") ?? ""),
            });
          }}
        >
          <label className="field">
            <span>Email</span>
            <input name="email" type="email" autoComplete="username" spellCheck={false} defaultValue="admin@appdaddy.win" />
          </label>
          <label className="field">
            <span>Password</span>
            <input name="password" type="password" autoComplete="current-password" />
          </label>
          <button className="button button-primary" type="submit" disabled={login.isPending}>
            {login.isPending ? "Signing in..." : "Sign in"}
          </button>
          {login.error ? <p className="error-text">{login.error.message}</p> : null}
        </form>
      </section>
    </main>
  );
}

function AuthenticatedApp({ user }: { user: AuthUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const hasEntityWorkspaces = user.memberships.length > 0;
  const hasEntityContext = hasEntityWorkspaces || Boolean(user.activeWorkspace.entityId);
  const activeEntityId = user.activeWorkspace.entityId ?? user.memberships[0]?.entity.id ?? null;
  const onMasterRoute = location.pathname.startsWith("/master");
  const activeEntity = user.memberships.find((membership) => membership.entity.id === activeEntityId)?.entity ?? null;
  const activeEntityName = activeEntity?.name ?? user.activeWorkspace.entityName ?? null;

  const logout = useMutation({
    mutationFn: () => api.logout(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
      navigate("/");
    },
  });
  const switchWorkspace = useMutation({
    mutationFn: ({ mode, entityId }: { mode: "master" | "entity"; entityId?: string | null }) => api.switchWorkspace(mode, entityId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });
  const stopImpersonation = useMutation({
    mutationFn: () => api.stopImpersonation(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
      navigate("/master");
    },
  });

  const primaryNav = user.globalRole && (!hasEntityContext || onMasterRoute) ? masterNav : entityNav;

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar-scroll">
          <div className="brand">
            <div className="brand-logo-stage">
              <img src="/roa-logo-signature-tight.png" alt="Retail Ops Analysis" />
            </div>
            <div className="brand-copy">
              <strong>ROA Stocktake</strong>
              <span>{onMasterRoute ? "ROA oversight" : activeEntityName ?? memberWorkspaceLabel}</span>
            </div>
          </div>

          <WorkspaceSwitcher
            user={user}
            hasEntityWorkspaces={hasEntityWorkspaces}
            hasEntityContext={hasEntityContext}
            busy={switchWorkspace.isPending || stopImpersonation.isPending}
            onChangeMode={(mode) => switchWorkspace.mutate({ mode, entityId: activeEntityId ?? undefined })}
            onChangeEntity={(entityId) => switchWorkspace.mutate({ mode: user.activeWorkspace.mode, entityId })}
            onStopImpersonation={() => stopImpersonation.mutate()}
            onOpenRoute={() => navigate(hasEntityContext ? (onMasterRoute ? "/dashboard" : "/master") : "/master/entities")}
            onMasterRoute={onMasterRoute}
          />

          <nav className="nav-list">
            <p className="nav-section">{onMasterRoute ? "ROA oversight" : memberWorkspaceLabel}</p>
            {primaryNav.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.to === "/master" || item.to === "/dashboard"} className="nav-link">
                <item.icon size={18} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="sidebar-footer">
          <div>
            <p className="sidebar-user">{user.name}</p>
            <p className="sidebar-meta">{user.globalRole ? "ROA access" : "Entity access"}</p>
          </div>
          <button className="button button-sidebar button-sidebar-quiet" onClick={() => logout.mutate()} disabled={logout.isPending}>
            Sign out
          </button>
        </div>
      </aside>

      <main className="main-content">
        <Routes>
          <Route path="/" element={<HomeRedirect user={user} />} />
          <Route path="/master" element={<MasterDashboardPage user={user} />} />
          <Route path="/master/entities" element={<EntityDirectoryPage user={user} />} />
          <Route path="/master/storage" element={<StoragePage />} />
          <Route path="/master/backups" element={<BackupsPage user={user} />} />
          <Route path="/dashboard" element={hasEntityContext ? <DashboardPage user={user} /> : <Navigate to="/master/entities" replace />} />
          <Route path="/board" element={hasEntityContext ? <BoardPage /> : <Navigate to="/master/entities" replace />} />
          <Route path="/stocktakes/:stocktakeId" element={hasEntityContext ? <StocktakePage user={user} /> : <Navigate to="/master/entities" replace />} />
          <Route path="/reviews" element={hasEntityContext ? <ReviewsPage user={user} /> : <Navigate to="/master/entities" replace />} />
          <Route path="/payroll" element={hasEntityContext ? <PayrollPage user={user} /> : <Navigate to="/master/entities" replace />} />
          <Route path="/setup" element={hasEntityContext ? <EntitySetupPage user={user} /> : <Navigate to="/master/entities" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function HomeRedirect({ user }: { user: AuthUser }) {
  if (user.globalRole && user.memberships.length === 0 && !user.activeWorkspace.entityId) {
    return <Navigate to="/master/entities" replace />;
  }
  return <Navigate to={user.activeWorkspace.mode === "master" ? "/master" : "/dashboard"} replace />;
}

function WorkspaceSwitcher({
  user,
  hasEntityWorkspaces,
  hasEntityContext,
  busy,
  onChangeMode,
  onChangeEntity,
  onStopImpersonation,
  onOpenRoute,
  onMasterRoute,
}: {
  user: AuthUser;
  hasEntityWorkspaces: boolean;
  hasEntityContext: boolean;
  busy: boolean;
  onChangeMode: (mode: "master" | "entity") => void;
  onChangeEntity: (entityId: string) => void;
  onStopImpersonation: () => void;
  onOpenRoute: () => void;
  onMasterRoute: boolean;
}) {
  const activeEntityId = user.activeWorkspace.entityId ?? user.memberships[0]?.entity.id ?? "";

  return (
    <div className="workspace-switcher">
      <div className="section-title compact">
        <h2>Mode</h2>
        {user.activeWorkspace.impersonatingEntityName ? <span className="pill pill-warn">Impersonating</span> : null}
      </div>
      {user.globalRole && hasEntityContext ? (
        <div className="workspace-mode-toggle" role="tablist" aria-label="Workspace mode">
          <button
            className={`mode-chip ${user.activeWorkspace.mode === "master" ? "mode-chip-active" : ""}`}
            onClick={() => onChangeMode("master")}
            disabled={busy}
            aria-pressed={user.activeWorkspace.mode === "master"}
          >
            <LayoutDashboard size={15} />
            Master
          </button>
          <button
            className={`mode-chip ${user.activeWorkspace.mode === "entity" ? "mode-chip-active" : ""}`}
            onClick={() => onChangeMode("entity")}
            disabled={busy}
            aria-pressed={user.activeWorkspace.mode === "entity"}
          >
            <Building2 size={15} />
            Entity
          </button>
        </div>
      ) : null}
      {hasEntityWorkspaces ? (
        <label className="field compact-field">
          <span>Active entity</span>
          <select value={activeEntityId} onChange={(event) => onChangeEntity(event.target.value)} disabled={busy}>
            {user.memberships.map((membership) => (
              <option key={membership.id} value={membership.entity.id}>
                {membership.entity.name}
              </option>
            ))}
          </select>
        </label>
      ) : hasEntityContext ? (
        <div className="workspace-current-entity">
          <span>Active entity</span>
          <strong>{user.activeWorkspace.entityName ?? "Current entity"}</strong>
        </div>
      ) : (
        <div className="empty-state empty-state-sidebar">
          <FolderOpen size={16} />
          <div>
            <strong>No members yet</strong>
            <p>Create your first member or resort workspace from the ROA oversight console to start defining structure and workflow.</p>
          </div>
        </div>
      )}
      {user.globalRole ? (
        <button className="button button-sidebar" onClick={onOpenRoute} disabled={busy}>
          {hasEntityContext ? (onMasterRoute ? "Open member" : "Open ROA") : "Create member"}
        </button>
      ) : null}
      {user.activeWorkspace.impersonatingEntityId ? (
        <button className="button button-sidebar" onClick={onStopImpersonation} disabled={busy}>
          Stop impersonation
        </button>
      ) : null}
    </div>
  );
}

function ErrorPanel({ message }: { message: string }) {
  return (
    <div className="page">
      <article className="card error-panel">
        <AlertCircle size={18} />
        <p>{message}</p>
      </article>
    </div>
  );
}

function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="empty-state">
      <FolderOpen size={18} />
      <div>
        <strong>{title}</strong>
        <p>{description}</p>
      </div>
    </div>
  );
}

function entityRolesForActiveWorkspace(user: AuthUser): EntityRole[] {
  const entityId = user.activeWorkspace.entityId;
  if (!entityId) {
    return [];
  }
  const membership = user.memberships.find((item) => item.entity.id === entityId);
  return membership ? [...new Set(membership.assignments.map((assignment) => assignment.role))] : [];
}

function canEditClientSide(user: AuthUser, summary: StocktakeSummary, stepNo: number) {
  if (user.globalRole === "master_admin" || user.globalRole === "master_ops") {
    return true;
  }
  const roles = entityRolesForActiveWorkspace(user);
  if (roles.includes("entity_admin")) return true;
  if (roles.includes("resort_ops")) return stepNo <= 6 || stepNo === 10 || stepNo === 11 || stepNo === 12 || stepNo === 15;
  if (roles.includes("regional_manager")) return stepNo === 8 || stepNo === 9;
  if (roles.includes("oan")) return stepNo === 7 || stepNo === 13;
  if (roles.includes("payroll")) return stepNo === 14;
  return false;
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1_073_741_824) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  return `${(bytes / 1_073_741_824).toFixed(2)} GB`;
}

function formatPercent(value: number) {
  return `${Math.round(value * 100)}%`;
}

function humanizeRole(role: EntityRole) {
  return entityRoleOptions.find((item) => item.value === role)?.label ?? role.replaceAll("_", " ");
}

function humanizeScopeType(scopeType: "entity" | "resort" | "outlet") {
  switch (scopeType) {
    case "entity":
      return "Member";
    case "resort":
      return "Resort";
    case "outlet":
      return "Outlet / Store";
  }
}

function describeAssignmentScope(scopeType: "entity" | "resort" | "outlet", scopeLabel?: string | null) {
  return scopeType === "entity" ? "Whole member" : scopeLabel ?? humanizeScopeType(scopeType);
}

function getStepCompletion(summary: StocktakeSummary) {
  return summary.steps.filter((step) => step.status === "complete").length;
}

function getProgress(summary: StocktakeSummary) {
  return summary.steps.length ? getStepCompletion(summary) / summary.steps.length : 0;
}

function getNextTemplate(summary: StocktakeSummary) {
  const nextStep = summary.steps.find((step) => step.status !== "complete");
  return nextStep ? workflowTemplateByStep.get(nextStep.stepNo) ?? null : null;
}

function getIssueLabel(summary: StocktakeSummary) {
  if (summary.computed.exceptionFlag) {
    return summary.computed.exceptionFlag;
  }
  if (summary.computed.payrollReady) {
    return "Ready for payroll";
  }
  if (summary.steps.every((step) => step.status === "complete")) {
    return "Complete";
  }
  return "On track";
}

function getIssueClass(summary: StocktakeSummary) {
  if (summary.computed.exceptionFlag) return "pill-danger";
  if (summary.computed.payrollReady) return "pill-warn";
  if (summary.steps.every((step) => step.status === "complete")) return "pill-good";
  return "";
}

function sortSummariesForAction(items: StocktakeSummary[]) {
  return [...items].sort((left, right) => {
    const leftWeight = left.computed.exceptionFlag ? 0 : left.computed.payrollReady ? 1 : 2;
    const rightWeight = right.computed.exceptionFlag ? 0 : right.computed.payrollReady ? 1 : 2;
    if (leftWeight !== rightWeight) {
      return leftWeight - rightWeight;
    }
    const progressDelta = getProgress(left) - getProgress(right);
    if (progressDelta !== 0) {
      return progressDelta;
    }
    return `${left.resort.name}-${left.outlet.name}`.localeCompare(`${right.resort.name}-${right.outlet.name}`);
  });
}

function buildActionRows(items: StocktakeSummary[]) {
  const map = new Map<string, StocktakeSummary>();
  for (const item of items) {
    map.set(item.stocktake.id, item);
  }
  return sortSummariesForAction([...map.values()]);
}

function buildStepViews(summary: StocktakeSummary, user: AuthUser): WorkflowStepView[] {
  return summary.steps
    .map((step) => {
      const template = workflowTemplateByStep.get(step.stepNo);
      if (!template) {
        return null;
      }
      const predecessorStatus = template.predecessorStepNo
        ? summary.steps.find((candidate) => candidate.stepNo === template.predecessorStepNo)?.status
        : "complete";
      return {
        step,
        template,
        blocked: Boolean(template.predecessorStepNo && predecessorStatus !== "complete"),
        canEdit: canEditClientSide(user, summary, step.stepNo),
      } satisfies WorkflowStepView;
    })
    .filter((item): item is WorkflowStepView => Boolean(item));
}

function StatCard({
  label,
  value,
  tone,
  hint,
}: {
  label: string;
  value: string | number;
  tone?: "default" | "good" | "warn" | "danger";
  hint?: string;
}) {
  return (
    <article className={`card metric-card tone-${tone ?? "default"}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {hint ? <p className="muted">{hint}</p> : null}
    </article>
  );
}

function StocktakeTable({
  items,
  showCycle = true,
  emptyMessage,
}: {
  items: StocktakeSummary[];
  showCycle?: boolean;
  emptyMessage: string;
}) {
  if (!items.length) {
    return <EmptyState title="Nothing to review" description={emptyMessage} />;
  }

  return (
    <div className="table-shell">
      <table className="data-table">
        <thead>
          <tr>
            {showCycle ? <th>Cycle</th> : null}
            <th>Resort</th>
            <th>Outlet</th>
            <th>Progress</th>
            <th>Next action</th>
            <th>Issue</th>
            <th>Open</th>
          </tr>
        </thead>
        <tbody>
          {items.map((summary) => {
            const completion = getStepCompletion(summary);
            const nextTemplate = getNextTemplate(summary);
            return (
              <tr key={summary.stocktake.id}>
                {showCycle ? <td>{summary.cycle.code}</td> : null}
                <td>{summary.resort.name}</td>
                <td>{summary.outlet.name}</td>
                <td>{completion}/{summary.steps.length}</td>
                <td>{nextTemplate?.action ?? "Final pack compiled"}</td>
                <td>
                  <span className={`pill ${getIssueClass(summary)}`}>{getIssueLabel(summary)}</span>
                </td>
                <td>
                  <NavLink className="table-link" to={`/stocktakes/${summary.stocktake.id}`}>
                    Open <ArrowRight size={14} />
                  </NavLink>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SectionTabs({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div className="segmented-control" role="tablist" aria-label="Section tabs">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={value === option.value}
          className={`segment ${value === option.value ? "active" : ""}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function MasterDashboardPage({ user }: { user: AuthUser }) {
  const overviewQuery = useQuery({
    queryKey: ["master", "overview"],
    queryFn: api.masterOverview,
  });

  if (overviewQuery.isLoading) {
    return <LoadingScreen label="Loading master overview" />;
  }

  if (overviewQuery.error || !overviewQuery.data) {
    return <ErrorPanel message={overviewQuery.error?.message ?? "Unable to load the master overview."} />;
  }

  const overview = overviewQuery.data;
  const topEntities = [...overview.entities]
    .sort((left, right) => right.exceptionCount - left.exceptionCount || right.storageBytes - left.storageBytes)
    .slice(0, 8);
  const recentBackups = [...overview.backupJobs]
    .sort((left, right) => right.startedAt.localeCompare(left.startedAt))
    .slice(0, 6);

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <p className="eyebrow">ROA master</p>
          <h1>Operational overview</h1>
          <p className="page-summary">
            ROA is the default oversight layer. Use this view to see which members or resorts need attention, how
            storage is trending, and whether backups are healthy before opening a specific workspace.
          </p>
        </div>
        <span className="pill">{user.globalRole?.replaceAll("_", " ")}</span>
      </header>

      <div className="metric-grid">
        {overview.metrics.map((metric) => (
          <StatCard key={metric.label} label={metric.label} value={metric.value} tone={metric.tone} />
        ))}
      </div>

      <div className="page-grid">
        <article className="card stack">
          <div className="section-title">
            <div>
              <h2>Member health</h2>
              <p className="muted">Prioritised by exceptions and storage footprint across member workspaces.</p>
            </div>
            <NavLink className="table-link" to="/master/entities">
              Manage members <ArrowRight size={14} />
            </NavLink>
          </div>
          {topEntities.length ? (
            <div className="table-shell">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{memberSingularLabel}</th>
                    <th>Resorts</th>
                    <th>Outlets</th>
                    <th>Exceptions</th>
                    <th>Storage</th>
                    <th>Latest backup</th>
                  </tr>
                </thead>
                <tbody>
                  {topEntities.map((item) => (
                    <tr key={item.entity.id}>
                      <td>
                        <div className="summary-stack">
                          <strong>{item.entity.name}</strong>
                          <span className="muted">{item.entity.shortCode}</span>
                        </div>
                      </td>
                      <td>{item.resortCount}</td>
                      <td>{item.outletCount}</td>
                      <td>{item.exceptionCount}</td>
                      <td>{formatBytes(item.storageBytes)}</td>
                      <td>{item.latestBackupAt ? new Date(item.latestBackupAt).toLocaleString() : "Never"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState title="No members yet" description="Create your first member or resort workspace below the ROA oversight layer." />
          )}
        </article>

        <article className="card stack">
          <div className="section-title">
            <div>
              <h2>Recent backups</h2>
              <p className="muted">Latest backup activity across all entities.</p>
            </div>
            <NavLink className="table-link" to="/master/backups">
              Open backups <ArrowRight size={14} />
            </NavLink>
          </div>
          {recentBackups.length ? (
            <div className="summary-stack">
              {recentBackups.map((job) => (
                <div key={job.id} className="detail-row">
                  <div className="summary-stack">
                    <strong>{job.entityName}</strong>
                    <span className="muted">{job.triggerMode} run</span>
                  </div>
                  <div className="summary-stack align-start">
                    <span className={`pill ${job.status === "failed" ? "pill-danger" : job.status === "completed" ? "pill-good" : "pill-warn"}`}>
                      {job.status}
                    </span>
                    <span className="muted">{new Date(job.startedAt).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title="No backups yet" description="Configure OneDrive in the backup settings to start scheduled or manual exports." />
          )}
        </article>
      </div>
    </section>
  );
}

function EntityDirectoryPage({ user }: { user: AuthUser }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const entitiesQuery = useQuery({
    queryKey: ["master", "entities"],
    queryFn: api.masterEntities,
  });

  const createEntity = useMutation({
    mutationFn: ({
      name,
      shortCode,
      supportEmail,
    }: {
      name: string;
      shortCode: string;
      supportEmail: string;
    }) =>
      api.saveEntity(
        { name, shortCode },
        {
          supportEmail: supportEmail || null,
        }
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["master", "entities"] }),
        queryClient.invalidateQueries({ queryKey: ["master", "overview"] }),
      ]);
    },
  });

  const openEntity = useMutation({
    mutationFn: async ({ entityId, route }: { entityId: string; route: "/dashboard" | "/setup" }) => {
      await api.startImpersonation(entityId);
      return route;
    },
    onSuccess: async (route) => {
      await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
      navigate(route);
    },
  });

  if (entitiesQuery.isLoading) {
    return <LoadingScreen label="Loading entities" />;
  }

  if (entitiesQuery.error || !entitiesQuery.data) {
    return <ErrorPanel message={entitiesQuery.error?.message ?? "Unable to load entities."} />;
  }

  const readOnly = user.globalRole === "master_readonly";

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Master setup</p>
          <h1>{memberDirectoryLabel}</h1>
          <p className="page-summary">
            ROA is always the default oversight company. Create only member or resort workspaces here, then define the
            resorts and outlets/stores that sit beneath them.
          </p>
        </div>
      </header>

      <div className="page-grid">
        <article className="card stack">
          <div className="section-title">
            <div>
              <h2>Add member / resort</h2>
              <p className="muted">ROA already exists as the built-in oversight layer. New member workspaces start blank so you can define the exact structure, documents, counters, and users as we build the process.</p>
            </div>
          </div>
          <form
            className="stack form-grid"
            onSubmit={(event) => {
              event.preventDefault();
              const formData = new FormData(event.currentTarget);
              createEntity.mutate({
                name: String(formData.get("name") ?? ""),
                shortCode: String(formData.get("shortCode") ?? ""),
                supportEmail: String(formData.get("supportEmail") ?? ""),
              });
              event.currentTarget.reset();
            }}
          >
            <label className="field">
              <span>Member / Resort name</span>
              <input name="name" required disabled={readOnly || createEntity.isPending} />
            </label>
            <label className="field">
              <span>Short code</span>
              <input name="shortCode" required maxLength={12} disabled={readOnly || createEntity.isPending} />
            </label>
            <label className="field">
              <span>Support email</span>
              <input name="supportEmail" type="email" autoComplete="email" disabled={readOnly || createEntity.isPending} />
            </label>
            <button className="button button-primary" type="submit" disabled={readOnly || createEntity.isPending}>
              {createEntity.isPending ? "Saving..." : "Create member"}
            </button>
            {createEntity.error ? <p className="error-text">{createEntity.error.message}</p> : null}
            {readOnly ? <p className="muted">Master read-only users can view members but cannot create them.</p> : null}
          </form>
        </article>

        <article className="card stack">
          <div className="section-title">
            <div>
              <h2>Member directory</h2>
              <p className="muted">Open a member workspace directly in operations mode or setup mode.</p>
            </div>
          </div>
          {entitiesQuery.data.length ? (
            <div className="table-shell">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{memberSingularLabel}</th>
                    <th>Users</th>
                    <th>Outlets</th>
                    <th>Exceptions</th>
                    <th>Storage</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entitiesQuery.data.map((item) => (
                    <tr key={item.entity.id}>
                      <td>
                        <div className="summary-stack">
                          <strong>{item.entity.name}</strong>
                          <span className="muted">{item.entity.shortCode}</span>
                        </div>
                      </td>
                      <td>{item.memberCount}</td>
                      <td>{item.outletCount}</td>
                      <td>{item.exceptionCount}</td>
                      <td>{formatBytes(item.storageBytes)}</td>
                      <td>
                        <div className="table-actions">
                          <button
                            className="table-button"
                            type="button"
                            onClick={() => openEntity.mutate({ entityId: item.entity.id, route: "/dashboard" })}
                            disabled={openEntity.isPending}
                          >
                            Open member
                          </button>
                          <button
                            className="table-button"
                            type="button"
                            onClick={() => openEntity.mutate({ entityId: item.entity.id, route: "/setup" })}
                            disabled={openEntity.isPending}
                          >
                            Setup
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState title="No members" description="Create one from the panel on the left to get started." />
          )}
        </article>
      </div>
    </section>
  );
}

function StoragePage() {
  const storageQuery = useQuery({
    queryKey: ["master", "storage"],
    queryFn: api.masterStorage,
  });

  if (storageQuery.isLoading) {
    return <LoadingScreen label="Loading storage report" />;
  }

  if (storageQuery.error || !storageQuery.data) {
    return <ErrorPanel message={storageQuery.error?.message ?? "Unable to load the storage report."} />;
  }

  const report = storageQuery.data;

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Master oversight</p>
          <h1>Storage usage</h1>
          <p className="page-summary">
            Track overall usage, see which members are growing fastest, and identify the main cycles or document types
            driving storage.
          </p>
        </div>
      </header>

      <div className="metric-grid">
        <StatCard label="Total storage" value={formatBytes(report.totalBytes)} />
        <StatCard label="Files stored" value={report.totalFiles} />
        <StatCard label="Members" value={report.entities.length} />
        <StatCard label="Updated" value={new Date(report.updatedAt).toLocaleString()} />
      </div>

      <article className="card stack">
        <div className="section-title">
          <div>
              <h2>Member breakdown</h2>
              <p className="muted">Each row includes the largest cycle and document bucket for that member workspace.</p>
            </div>
          </div>
        {report.entities.length ? (
          <div className="table-shell">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{memberSingularLabel}</th>
                  <th>Total storage</th>
                  <th>Files</th>
                  <th>Largest cycle</th>
                  <th>Top document type</th>
                  <th>Top outlet</th>
                </tr>
              </thead>
              <tbody>
                {report.entities.map((entity) => (
                  <tr key={entity.entityId}>
                    <td>{entity.entityName}</td>
                    <td>{formatBytes(entity.totalBytes)}</td>
                    <td>{entity.fileCount}</td>
                    <td>{entity.byCycle[0] ? `${entity.byCycle[0].label} (${formatBytes(entity.byCycle[0].totalBytes)})` : "No files"}</td>
                    <td>
                      {entity.byDocumentType[0]
                        ? `${entity.byDocumentType[0].label.replaceAll("_", " ")} (${formatBytes(entity.byDocumentType[0].totalBytes)})`
                        : "No files"}
                    </td>
                    <td>{entity.byOutlet[0] ? entity.byOutlet[0].label : "No files"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title="No uploads yet" description="Storage reporting will populate as evidence gets uploaded." />
        )}
      </article>
    </section>
  );
}

function BackupsPage({ user }: { user: AuthUser }) {
  const queryClient = useQueryClient();
  const backupsQuery = useQuery({
    queryKey: ["master", "backups"],
    queryFn: api.masterBackups,
  });
  const entitiesQuery = useQuery({
    queryKey: ["master", "entities"],
    queryFn: api.masterEntities,
  });

  const saveTarget = useMutation({
    mutationFn: api.saveBackupTarget,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["master", "backups"] }),
        queryClient.invalidateQueries({ queryKey: ["master", "overview"] }),
      ]);
    },
  });
  const runBackup = useMutation({
    mutationFn: api.runBackup,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["master", "backups"] }),
        queryClient.invalidateQueries({ queryKey: ["master", "overview"] }),
      ]);
    },
  });

  if (backupsQuery.isLoading || entitiesQuery.isLoading) {
    return <LoadingScreen label="Loading backup controls" />;
  }

  if (backupsQuery.error || !backupsQuery.data || entitiesQuery.error || !entitiesQuery.data) {
    return (
      <ErrorPanel
        message={backupsQuery.error?.message ?? entitiesQuery.error?.message ?? "Unable to load backup settings."}
      />
    );
  }

  const readOnly = user.globalRole === "master_readonly";
  const target = backupsQuery.data.target;
  const jobs = [...backupsQuery.data.jobs].sort((left, right) => right.startedAt.localeCompare(left.startedAt));

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Master oversight</p>
          <h1>Backups</h1>
          <p className="page-summary">
            Configure a single OneDrive target for ROA, monitor job history, and run backups on demand per member workspace.
          </p>
        </div>
      </header>

      <div className="metric-grid">
        <StatCard label="Backup target" value={target?.configured ? "Connected" : "Not configured"} tone={target?.configured ? "good" : "warn"} />
        <StatCard label="Target enabled" value={target?.enabled ? "Yes" : "No"} tone={target?.enabled ? "good" : "default"} />
        <StatCard label="Runs completed" value={jobs.filter((job) => job.status === "completed").length} />
        <StatCard label="Runs failed" value={jobs.filter((job) => job.status === "failed").length} tone={jobs.some((job) => job.status === "failed") ? "danger" : "good"} />
      </div>

      <div className="page-grid">
        <article className="card stack">
          <div className="section-title">
            <div>
              <h2>OneDrive target</h2>
              <p className="muted">Credentials are stored once and reused for scheduled or manual exports.</p>
            </div>
          </div>
          <form
            className="stack form-grid"
            onSubmit={(event) => {
              event.preventDefault();
              const formData = new FormData(event.currentTarget);
              saveTarget.mutate({
                tenantId: String(formData.get("tenantId") ?? ""),
                clientId: String(formData.get("clientId") ?? ""),
                clientSecret: String(formData.get("clientSecret") ?? ""),
                driveId: String(formData.get("driveId") ?? ""),
                rootFolder: String(formData.get("rootFolder") ?? ""),
                schedule: String(formData.get("schedule") ?? ""),
                enabled: formData.get("enabled") === "on",
              });
            }}
          >
            <label className="field">
              <span>Tenant ID</span>
              <input name="tenantId" autoComplete="off" defaultValue={target?.tenantId ?? ""} disabled={readOnly || saveTarget.isPending} />
            </label>
            <label className="field">
              <span>Client ID</span>
              <input name="clientId" autoComplete="off" defaultValue={target?.clientId ?? ""} disabled={readOnly || saveTarget.isPending} />
            </label>
            <label className="field">
              <span>Client secret</span>
              <input
                name="clientSecret"
                type="password"
                autoComplete="new-password"
                placeholder={target?.clientSecretConfigured ? "Stored already. Enter only to replace." : ""}
                disabled={readOnly || saveTarget.isPending}
              />
            </label>
            <label className="field">
              <span>Drive ID</span>
              <input name="driveId" autoComplete="off" defaultValue={target?.driveId ?? ""} disabled={readOnly || saveTarget.isPending} />
            </label>
            <label className="field">
              <span>Root folder</span>
              <input name="rootFolder" autoComplete="off" defaultValue={target?.rootFolder ?? ""} disabled={readOnly || saveTarget.isPending} />
            </label>
            <label className="field">
              <span>Schedule</span>
              <input name="schedule" autoComplete="off" defaultValue={target?.schedule ?? "0 2 * * 1"} disabled={readOnly || saveTarget.isPending} />
            </label>
            <label className="checkbox-row">
              <input type="checkbox" name="enabled" defaultChecked={target?.enabled ?? false} disabled={readOnly || saveTarget.isPending} />
              <span>Enable backup target</span>
            </label>
            <button className="button button-primary" type="submit" disabled={readOnly || saveTarget.isPending}>
              {saveTarget.isPending ? "Saving..." : "Save backup target"}
            </button>
            {saveTarget.error ? <p className="error-text">{saveTarget.error.message}</p> : null}
          </form>
        </article>

        <article className="card stack">
          <div className="section-title">
            <div>
              <h2>Manual run</h2>
              <p className="muted">Use this for a spot backup before major changes or after month-end close.</p>
            </div>
          </div>
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              const formData = new FormData(event.currentTarget);
              runBackup.mutate(String(formData.get("entityId") ?? ""));
            }}
          >
            <label className="field">
              <span>Member / Resort</span>
              <select name="entityId" defaultValue={entitiesQuery.data[0]?.entity.id ?? ""} disabled={readOnly || runBackup.isPending}>
                {entitiesQuery.data.map((item) => (
                  <option key={item.entity.id} value={item.entity.id}>
                    {item.entity.name}
                  </option>
                ))}
              </select>
            </label>
            <button className="button button-primary" type="submit" disabled={readOnly || runBackup.isPending || !target?.enabled}>
              {runBackup.isPending ? "Running backup..." : "Run backup now"}
            </button>
            {!target?.enabled ? <p className="muted">Enable and validate the OneDrive target before running exports.</p> : null}
            {runBackup.error ? <p className="error-text">{runBackup.error.message}</p> : null}
          </form>
        </article>
      </div>

      <article className="card stack">
        <div className="section-title">
          <div>
            <h2>Backup history</h2>
            <p className="muted">Most recent jobs first.</p>
          </div>
        </div>
        {jobs.length ? (
          <div className="table-shell">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Entity</th>
                  <th>Status</th>
                  <th>Trigger</th>
                  <th>Files</th>
                  <th>Size</th>
                  <th>Started</th>
                  <th>Completed</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.id}>
                    <td>{job.entityName}</td>
                    <td>
                      <span className={`pill ${job.status === "failed" ? "pill-danger" : job.status === "completed" ? "pill-good" : "pill-warn"}`}>
                        {job.status}
                      </span>
                    </td>
                    <td>{job.triggerMode}</td>
                    <td>{job.fileCount}</td>
                    <td>{formatBytes(job.totalBytes)}</td>
                    <td>{new Date(job.startedAt).toLocaleString()}</td>
                    <td>{job.completedAt ? new Date(job.completedAt).toLocaleString() : "In progress"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title="No backup jobs" description="Manual and scheduled backup history will appear here." />
        )}
      </article>
    </section>
  );
}

function DashboardPage({ user }: { user: AuthUser }) {
  const dashboardQuery = useQuery({
    queryKey: ["dashboard"],
    queryFn: api.dashboard,
  });

  if (dashboardQuery.isLoading) {
    return <LoadingScreen label="Loading dashboard" />;
  }

  if (dashboardQuery.error || !dashboardQuery.data) {
    return <ErrorPanel message={dashboardQuery.error?.message ?? "Unable to load the dashboard."} />;
  }

  const dashboard = dashboardQuery.data;
  const actionRows = buildActionRows([...dashboard.myQueue, ...dashboard.exceptions, ...dashboard.payrollQueue]).slice(0, 16);
  const metricMap = new Map(dashboard.metrics.map((metric) => [metric.label, metric.value]));

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <p className="eyebrow">{dashboard.entity?.name ?? memberWorkspaceLabel}</p>
          <h1>Dashboard</h1>
          <p className="page-summary">
            Focus on what needs action next instead of juggling separate queue walls. This view combines work needing
            attention, exceptions, and payroll-ready outlets into one operating surface.
          </p>
        </div>
        {dashboard.currentCycle ? <span className="pill">{dashboard.currentCycle.label}</span> : null}
      </header>

      <div className="metric-grid">
        {dashboard.metrics.map((metric) => (
          <StatCard key={metric.label} label={metric.label} value={metric.value} tone={metric.tone} />
        ))}
      </div>

      <div className="page-grid">
        <article className="card stack">
          <div className="section-title">
            <div>
              <h2>Action list</h2>
              <p className="muted">The most relevant outlets across your current queue.</p>
            </div>
            <NavLink className="table-link" to="/board">
              Open stocktake board <ArrowRight size={14} />
            </NavLink>
          </div>
          <StocktakeTable
            items={actionRows}
            showCycle={false}
            emptyMessage="Your current workspace has no outlets waiting on action."
          />
        </article>

        <article className="card stack">
          <div className="section-title">
            <div>
              <h2>Queue summary</h2>
              <p className="muted">How the current cycle is stacking up.</p>
            </div>
          </div>
          <div className="summary-stack">
            <div className="detail-row">
              <span>Work queue preview</span>
              <strong>{dashboard.myQueue.length}</strong>
            </div>
            <div className="detail-row">
              <span>Exceptions preview</span>
              <strong>{dashboard.exceptions.length}</strong>
            </div>
            <div className="detail-row">
              <span>Payroll-ready preview</span>
              <strong>{dashboard.payrollQueue.length}</strong>
            </div>
            <div className="detail-row">
              <span>Accessible stocktakes</span>
              <strong>{metricMap.get("Accessible stocktakes") ?? 0}</strong>
            </div>
            <div className="detail-row">
              <span>Read-only imports</span>
              <strong>{metricMap.get("Read-only imports") ?? 0}</strong>
            </div>
          </div>
          <div className="callout">
            <LayoutDashboard size={18} />
            <p>
              Use the board for bulk triage, the stocktake detail page for one outlet at a time, and the review/payroll
              queues only when a formal approval is needed.
            </p>
          </div>
        </article>
      </div>

      <article className="card stack">
        <div className="section-title">
          <div>
            <h2>Cycle health</h2>
            <p className="muted">Completion and exception rates across available cycles.</p>
          </div>
        </div>
        {dashboard.cycleSummaries.length ? (
          <div className="table-shell">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Cycle</th>
                  <th>Completed</th>
                  <th>Total</th>
                  <th>Completion</th>
                  <th>Exceptions</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.cycleSummaries.map((item) => (
                  <tr key={item.cycle.id}>
                    <td>{item.cycle.label}</td>
                    <td>{item.completed}</td>
                    <td>{item.total}</td>
                    <td>{formatPercent(item.completionRate)}</td>
                    <td>{item.exceptionCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title="No cycles" description="Cycle health appears once stocktake cycles are available for the entity." />
        )}
      </article>
    </section>
  );
}

function BoardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "attention" | "not_started" | "in_progress" | "payroll" | "complete">("all");
  const [showAll, setShowAll] = useState(false);
  const cycleId = searchParams.get("cycleId") ?? "";

  const cyclesQuery = useQuery({
    queryKey: ["cycles", cycleId],
    queryFn: () => api.cycles(cycleId || undefined),
  });

  const filteredItems = useMemo(() => {
    const summaries = cyclesQuery.data?.summaries ?? [];
    const normalizedSearch = searchText.trim().toLowerCase();
    return summaries.filter((summary) => {
      const matchesSearch =
        !normalizedSearch ||
        `${summary.resort.name} ${summary.outlet.name} ${summary.cycle.code}`.toLowerCase().includes(normalizedSearch);
      if (!matchesSearch) {
        return false;
      }
      switch (statusFilter) {
        case "attention":
          return Boolean(summary.computed.exceptionFlag);
        case "not_started":
          return getStepCompletion(summary) === 0;
        case "in_progress":
          return (
            getStepCompletion(summary) > 0 &&
            !summary.computed.exceptionFlag &&
            !summary.computed.payrollReady &&
            !summary.steps.every((step) => step.status === "complete")
          );
        case "payroll":
          return summary.computed.payrollReady;
        case "complete":
          return summary.steps.every((step) => step.status === "complete");
        default:
          return true;
      }
    });
  }, [cyclesQuery.data?.summaries, searchText, statusFilter]);

  if (cyclesQuery.isLoading) {
    return <LoadingScreen label="Loading stocktake board" />;
  }

  if (cyclesQuery.error || !cyclesQuery.data) {
    return <ErrorPanel message={cyclesQuery.error?.message ?? "Unable to load the stocktake board."} />;
  }

  const visibleItems = showAll ? filteredItems : filteredItems.slice(0, 50);

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <p className="eyebrow">{memberWorkspaceLabel}</p>
          <h1>Stocktake board</h1>
          <p className="page-summary">
            Search, filter, and open outlets quickly without scrolling through an unstructured wall of cards.
          </p>
        </div>
      </header>

      <div className="filter-bar">
        <label className="search-field">
          <Search size={16} />
          <input
            className="search-input"
            placeholder="Search resort, outlet, or cycle..."
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
        </label>
        <label className="field compact-field">
          <span>Cycle</span>
          <select
            value={cycleId}
            onChange={(event) => {
              const next = new URLSearchParams(searchParams);
              if (event.target.value) {
                next.set("cycleId", event.target.value);
              } else {
                next.delete("cycleId");
              }
              setSearchParams(next);
            }}
          >
            <option value="">All cycles</option>
            {cyclesQuery.data.cycles.map((cycle) => (
              <option key={cycle.id} value={cycle.id}>
                {cycle.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <SectionTabs
        value={statusFilter}
        onChange={(value) => setStatusFilter(value as typeof statusFilter)}
        options={[
          { value: "all", label: "All" },
          { value: "attention", label: "Attention" },
          { value: "not_started", label: "Not started" },
          { value: "in_progress", label: "In progress" },
          { value: "payroll", label: "Payroll" },
          { value: "complete", label: "Complete" },
        ]}
      />

      <div className="metric-grid">
        <StatCard label="Visible results" value={filteredItems.length} />
        <StatCard label="Exceptions" value={filteredItems.filter((item) => item.computed.exceptionFlag).length} tone="danger" />
        <StatCard label="Payroll ready" value={filteredItems.filter((item) => item.computed.payrollReady).length} tone="warn" />
        <StatCard label="Completed" value={filteredItems.filter((item) => item.steps.every((step) => step.status === "complete")).length} tone="good" />
      </div>

      <article className="card stack">
        <StocktakeTable
          items={visibleItems}
          showCycle={!cycleId}
          emptyMessage="Try broadening the filters or switching to another cycle."
        />
        {filteredItems.length > 50 ? (
          <div className="table-footer">
            <span className="muted">
              Showing {visibleItems.length} of {filteredItems.length} outlets.
            </span>
            <button className="button button-secondary" type="button" onClick={() => setShowAll((current) => !current)}>
              {showAll ? "Show first 50" : "Show all"}
            </button>
          </div>
        ) : null}
      </article>
    </section>
  );
}

function StocktakePage({ user }: { user: AuthUser }) {
  const { stocktakeId = "" } = useParams();
  const queryClient = useQueryClient();
  const stocktakeQuery = useQuery({
    queryKey: ["stocktake", stocktakeId],
    queryFn: () => api.stocktake(stocktakeId),
    enabled: Boolean(stocktakeId),
  });

  const [selectedStepNo, setSelectedStepNo] = useState<number | null>(null);
  const [draftNote, setDraftNote] = useState("");
  const [selectedDocumentType, setSelectedDocumentType] = useState("");
  const [metadataText, setMetadataText] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [aiSummary, setAiSummary] = useState<{ mode: "ai" | "fallback"; text: string } | null>(null);

  const refreshWorkspace = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["stocktake", stocktakeId] }),
      queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
      queryClient.invalidateQueries({ queryKey: ["cycles"] }),
      queryClient.invalidateQueries({ queryKey: ["reviews"] }),
      queryClient.invalidateQueries({ queryKey: ["payroll"] }),
    ]);
  };

  const stepMutation = useMutation({
    mutationFn: ({ stepNo, body }: { stepNo: number; body: Record<string, unknown> }) => api.updateStep(stocktakeId, stepNo, body),
    onSuccess: refreshWorkspace,
  });

  const uploadMutation = useMutation({
    mutationFn: (formData: FormData) => api.upload(formData),
    onSuccess: async () => {
      setMetadataText("");
      setSelectedFile(null);
      await refreshWorkspace();
    },
  });

  const aiMutation = useMutation({
    mutationFn: () => api.stocktakeAiSummary(stocktakeId),
    onSuccess: (result) => setAiSummary(result),
  });

  useEffect(() => {
    if (!stocktakeQuery.data) {
      return;
    }
    setAiSummary(stocktakeQuery.data.aiSummary);
    const nextIncomplete = stocktakeQuery.data.steps.find((step) => step.status !== "complete")?.stepNo ?? stocktakeQuery.data.steps[0]?.stepNo ?? null;
    setSelectedStepNo((current) => {
      if (current && stocktakeQuery.data.steps.some((step) => step.stepNo === current)) {
        return current;
      }
      return nextIncomplete;
    });
    setSelectedDocumentType((current) => {
      if (current) {
        return current;
      }
      return (
        stocktakeQuery.data.computed.missingDocuments[0] ??
        stocktakeQuery.data.requiredDocuments.find((document) => document.required)?.documentType ??
        ""
      );
    });
  }, [stocktakeQuery.data]);

  const stepViews = useMemo(
    () => (stocktakeQuery.data ? buildStepViews(stocktakeQuery.data, user) : []),
    [stocktakeQuery.data, user]
  );
  const selectedView = stepViews.find((item) => item.step.stepNo === selectedStepNo) ?? stepViews[0] ?? null;

  useEffect(() => {
    setDraftNote(selectedView?.step.notes ?? "");
  }, [selectedView?.step.id]);

  if (stocktakeQuery.isLoading) {
    return <LoadingScreen label="Loading stocktake" />;
  }

  if (stocktakeQuery.error || !stocktakeQuery.data) {
    return <ErrorPanel message={stocktakeQuery.error?.message ?? "Unable to load the stocktake."} />;
  }

  const summary = stocktakeQuery.data;
  const uploadedTypes = new Set(summary.uploads.filter((upload) => upload.status !== "archived").map((upload) => upload.documentType));

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <p className="eyebrow">{summary.cycle.label}</p>
          <h1>
            {summary.resort.name} - {summary.outlet.name}
          </h1>
          <p className="page-summary">
            Work through one gate at a time, keep evidence complete, and use the AI summary only as a helper rather than
            the source of truth.
          </p>
        </div>
        <span className={`pill ${getIssueClass(summary)}`}>{getIssueLabel(summary)}</span>
      </header>

      {summary.stocktake.readOnly ? (
        <div className="callout callout-warn">
          <AlertCircle size={18} />
          <p>This imported stocktake is read-only. Use it as reference data only.</p>
        </div>
      ) : null}

      <div className="metric-grid">
        <StatCard label="Progress" value={`${getStepCompletion(summary)}/${summary.steps.length}`} />
        <StatCard label="Missing documents" value={summary.computed.missingDocuments.length} tone={summary.computed.missingDocuments.length ? "warn" : "good"} />
        <StatCard label="Current gate" value={selectedView?.template.action ?? "Final pack"} />
        <StatCard label="Payroll status" value={summary.payrollSubmission?.status ?? (summary.computed.payrollReady ? "ready" : "not ready")} tone={summary.computed.payrollReady ? "warn" : "default"} />
      </div>

      <div className="page-grid">
        <article className="card stack">
          <div className="section-title">
            <div>
              <h2>Evidence control</h2>
              <p className="muted">Keep uploads structured so the final pack can be rebuilt in SOP order.</p>
            </div>
          </div>
          <div className="checklist">
            {summary.requiredDocuments.map((document) => {
              const uploadsForDocument = summary.uploads.filter((upload) => upload.documentType === document.documentType);
              return (
                <div key={document.id} className="checklist-row">
                  <div className="summary-stack">
                    <strong>{document.label}</strong>
                    <span className="muted">{document.notes || document.documentType.replaceAll("_", " ")}</span>
                  </div>
                  <div className="summary-stack align-start">
                    <span className={`pill ${uploadedTypes.has(document.documentType) ? "pill-good" : document.required ? "pill-warn" : ""}`}>
                      {uploadedTypes.has(document.documentType) ? "Uploaded" : document.required ? "Missing" : "Optional"}
                    </span>
                    <span className="muted">{uploadsForDocument.length} files</span>
                  </div>
                </div>
              );
            })}
          </div>

          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              if (!selectedFile) {
                return;
              }
              const formData = new FormData();
              formData.set("stocktakeId", stocktakeId);
              formData.set("documentType", selectedDocumentType);
              formData.set("metadataText", metadataText);
              formData.set("file", selectedFile);
              uploadMutation.mutate(formData);
            }}
          >
            <label className="field">
              <span>Document type</span>
              <select value={selectedDocumentType} onChange={(event) => setSelectedDocumentType(event.target.value)} disabled={summary.stocktake.readOnly || uploadMutation.isPending}>
                {summary.requiredDocuments.map((document) => (
                  <option key={document.id} value={document.documentType}>
                    {document.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Supporting note</span>
              <textarea
                rows={3}
                value={metadataText}
                onChange={(event) => setMetadataText(event.target.value)}
                placeholder="Add any key metadata you want visible to reviewers."
                disabled={summary.stocktake.readOnly || uploadMutation.isPending}
              />
            </label>
            <label className="field">
              <span>File</span>
              <input
                type="file"
                onChange={(event) => setSelectedFile(event.currentTarget.files?.[0] ?? null)}
                disabled={summary.stocktake.readOnly || uploadMutation.isPending}
              />
            </label>
            <button className="button button-primary" type="submit" disabled={!selectedFile || summary.stocktake.readOnly || uploadMutation.isPending}>
              <UploadCloud size={16} />
              {uploadMutation.isPending ? "Uploading..." : "Upload evidence"}
            </button>
            {uploadMutation.error ? <p className="error-text">{uploadMutation.error.message}</p> : null}
          </form>
        </article>

        <article className="card stack">
          <div className="section-title">
            <div>
              <h2>AI compliance copilot</h2>
              <p className="muted">Assistive only. Workflow rules and approvals still decide the outcome.</p>
            </div>
            <button className="button button-secondary" type="button" onClick={() => aiMutation.mutate()} disabled={aiMutation.isPending}>
              {aiMutation.isPending ? "Running AI..." : "Run AI check"}
            </button>
          </div>
          <div className="callout">
            <Workflow size={18} />
            <p>{aiSummary?.text ?? "No AI summary is available yet."}</p>
          </div>
          <div className="detail-list">
            <div className="detail-row">
              <span>Mode</span>
              <strong>{aiSummary?.mode ?? "fallback"}</strong>
            </div>
            <div className="detail-row">
              <span>Missing docs</span>
              <strong>{summary.computed.missingDocuments.length}</strong>
            </div>
            <div className="detail-row">
              <span>Open reviews</span>
              <strong>{summary.reviews.filter((review) => review.status !== "approved").length}</strong>
            </div>
          </div>
        </article>
      </div>

      <div className="step-workspace">
        <article className="card stack">
          <div className="section-title">
            <div>
              <h2>Workflow navigator</h2>
              <p className="muted">Choose a gate to inspect or update.</p>
            </div>
          </div>
          <div className="phase-stack">
            {phaseOrder.map((phase) => {
              const phaseSteps = stepViews.filter((item) => item.template.phase === phase);
              if (!phaseSteps.length) {
                return null;
              }
              return (
                <section key={phase} className="phase-group">
                  <div className="section-title compact">
                    <h2>{phase}</h2>
                  </div>
                  <div className="step-choice-list">
                    {phaseSteps.map((item) => (
                      <button
                        key={item.step.id}
                        type="button"
                        className={`step-choice ${selectedView?.step.id === item.step.id ? "active" : ""}`}
                        onClick={() => setSelectedStepNo(item.step.stepNo)}
                      >
                        <div className="summary-stack">
                          <strong>
                            {item.step.stepNo}. {item.template.action}
                          </strong>
                          <span className="muted">
                            {item.template.ownerTitle} - due by day {item.template.dueDay}
                          </span>
                        </div>
                        <span className={`pill ${item.blocked ? "pill-danger" : item.step.status === "complete" ? "pill-good" : item.step.status === "in_progress" ? "pill-warn" : ""}`}>
                          {item.blocked ? "Blocked" : item.step.status.replaceAll("_", " ")}
                        </span>
                      </button>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        </article>

        <article className="card stack">
          {selectedView ? (
            <>
              <div className="section-title">
                <div>
                  <h2>
                    Step {selectedView.step.stepNo}: {selectedView.template.action}
                  </h2>
                  <p className="muted">Owner: {selectedView.template.ownerTitle}</p>
                </div>
                <span className={`pill ${selectedView.step.status === "complete" ? "pill-good" : selectedView.blocked ? "pill-danger" : "pill-warn"}`}>
                  {selectedView.blocked ? "Blocked" : selectedView.step.status.replaceAll("_", " ")}
                </span>
              </div>

              {selectedView.blocked ? (
                <div className="callout callout-warn">
                  <AlertCircle size={18} />
                  <p>Complete step {selectedView.template.predecessorStepNo} before progressing this gate.</p>
                </div>
              ) : null}

              {!selectedView.canEdit ? (
                <div className="callout">
                  <ShieldCheck size={18} />
                  <p>Your current role can review this step but cannot update it.</p>
                </div>
              ) : null}

              <div className="detail-list">
                <div className="detail-row">
                  <span>Phase</span>
                  <strong>{selectedView.template.phase}</strong>
                </div>
                <div className="detail-row">
                  <span>Required evidence</span>
                  <strong>{selectedView.template.requiredDocument}</strong>
                </div>
                <div className="detail-row">
                  <span>Due day</span>
                  <strong>{selectedView.template.dueDay}</strong>
                </div>
                <div className="detail-row">
                  <span>Current owner</span>
                  <strong>{selectedView.step.ownerName || "Not assigned"}</strong>
                </div>
                <div className="detail-row">
                  <span>Last sign-off</span>
                  <strong>
                    {selectedView.step.signOffDate
                      ? `${selectedView.step.signOffName ?? "Signed"} on ${selectedView.step.signOffDate}`
                      : "Not signed off"}
                  </strong>
                </div>
              </div>

              <label className="field">
                <span>Step note</span>
                <textarea
                  rows={6}
                  value={draftNote}
                  onChange={(event) => setDraftNote(event.target.value)}
                  disabled={summary.stocktake.readOnly || !selectedView.canEdit || selectedView.blocked || stepMutation.isPending}
                />
              </label>

              <div className="button-row">
                <button
                  className="button button-secondary"
                  type="button"
                  disabled={summary.stocktake.readOnly || !selectedView.canEdit || selectedView.blocked || stepMutation.isPending}
                  onClick={() =>
                    stepMutation.mutate({
                      stepNo: selectedView.step.stepNo,
                      body: { status: "in_progress", notes: draftNote },
                    })
                  }
                >
                  <Clock3 size={16} />
                  Start step
                </button>
                <button
                  className="button button-secondary"
                  type="button"
                  disabled={summary.stocktake.readOnly || !selectedView.canEdit || stepMutation.isPending}
                  onClick={() =>
                    stepMutation.mutate({
                      stepNo: selectedView.step.stepNo,
                      body: { notes: draftNote },
                    })
                  }
                >
                  Save note
                </button>
                <button
                  className="button button-primary"
                  type="button"
                  disabled={summary.stocktake.readOnly || !selectedView.canEdit || selectedView.blocked || stepMutation.isPending}
                  onClick={() =>
                    stepMutation.mutate({
                      stepNo: selectedView.step.stepNo,
                      body: { status: "complete", notes: draftNote },
                    })
                  }
                >
                  <CheckCircle2 size={16} />
                  Mark complete
                </button>
              </div>
              {stepMutation.error ? <p className="error-text">{stepMutation.error.message}</p> : null}
            </>
          ) : (
            <EmptyState title="No workflow steps" description="This stocktake has no configured workflow steps." />
          )}
        </article>
      </div>

      <div className="detail-grid">
        <article className="card stack">
          <div className="section-title">
            <h2>Reviews</h2>
          </div>
          {summary.reviews.length ? (
            <div className="summary-stack">
              {summary.reviews.map((review) => (
                <div key={review.id} className="detail-row">
                  <div className="summary-stack">
                    <strong>{review.reviewType.toUpperCase()}</strong>
                    <span className="muted">{review.summary || "No summary"}</span>
                  </div>
                  <span className={`pill ${review.status === "approved" ? "pill-good" : review.status === "rejected" ? "pill-danger" : "pill-warn"}`}>
                    {review.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title="No reviews yet" description="Manager and OAN review history will appear here." />
          )}
        </article>

        <article className="card stack">
          <div className="section-title">
            <h2>Queries and deductions</h2>
          </div>
          {summary.queries.length || summary.deductions.length ? (
            <div className="summary-stack">
              {summary.queries.map((query) => (
                <div key={query.id} className="detail-row">
                  <div className="summary-stack">
                    <strong>Query for step {query.stepNo}</strong>
                    <span className="muted">{query.reason}</span>
                  </div>
                  <span className={`pill ${query.status === "rejected" ? "pill-danger" : query.status === "approved" ? "pill-good" : "pill-warn"}`}>
                    {query.status}
                  </span>
                </div>
              ))}
              {summary.deductions.map((deduction) => (
                <div key={deduction.id} className="detail-row">
                  <div className="summary-stack">
                    <strong>{deduction.employeeName}</strong>
                    <span className="muted">{deduction.employeeNumber}</span>
                  </div>
                  <strong>{deduction.amount.toFixed(2)}</strong>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title="Nothing raised" description="Queries and deductions will appear once they are added to the stocktake." />
          )}
        </article>
      </div>
    </section>
  );
}

function ReviewsPage({ user }: { user: AuthUser }) {
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState("");
  const [tab, setTab] = useState<"all" | "manager" | "oan">("all");
  const [showAll, setShowAll] = useState(false);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const reviewsQuery = useQuery({
    queryKey: ["reviews"],
    queryFn: api.reviews,
  });

  const managerMutation = useMutation({
    mutationFn: ({ stocktakeId, action, reason }: { stocktakeId: string; action: "approve" | "reject"; reason: string }) =>
      api.managerReview(stocktakeId, action, reason),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["reviews"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
        queryClient.invalidateQueries({ queryKey: ["stocktake"] }),
        queryClient.invalidateQueries({ queryKey: ["payroll"] }),
      ]);
    },
  });
  const oanMutation = useMutation({
    mutationFn: ({ stocktakeId, action, notesText }: { stocktakeId: string; action: "verify" | "return"; notesText: string }) =>
      api.oanReview(stocktakeId, action, notesText),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["reviews"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
        queryClient.invalidateQueries({ queryKey: ["stocktake"] }),
        queryClient.invalidateQueries({ queryKey: ["payroll"] }),
      ]);
    },
  });

  if (reviewsQuery.isLoading) {
    return <LoadingScreen label="Loading review queue" />;
  }

  if (reviewsQuery.error || !reviewsQuery.data) {
    return <ErrorPanel message={reviewsQuery.error?.message ?? "Unable to load the review queue."} />;
  }

  const roles = entityRolesForActiveWorkspace(user);
  const canManager = Boolean(user.globalRole && user.globalRole !== "master_readonly") || roles.includes("entity_admin") || roles.includes("regional_manager");
  const canOan = Boolean(user.globalRole && user.globalRole !== "master_readonly") || roles.includes("entity_admin") || roles.includes("oan");

  const filteredItems = reviewsQuery.data.filter((summary) => {
    const normalizedSearch = searchText.trim().toLowerCase();
    const matchesSearch =
      !normalizedSearch ||
      `${summary.resort.name} ${summary.outlet.name} ${summary.cycle.code}`.toLowerCase().includes(normalizedSearch);
    if (!matchesSearch) {
      return false;
    }
    if (tab === "manager") {
      return summary.steps.some((step) => step.stepNo === 9 && step.status !== "complete");
    }
    if (tab === "oan") {
      return summary.steps.some((step) => step.stepNo === 13 && step.status !== "complete");
    }
    return true;
  });
  const visibleItems = showAll ? filteredItems : filteredItems.slice(0, 40);
  const inlineReviewTab = tab !== "all";

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Member workflow</p>
          <h1>Review queue</h1>
          <p className="page-summary">
            Keep approvals and returns focused in one queue instead of mixing them into the general dashboard.
          </p>
        </div>
      </header>

      <div className="filter-bar">
        <label className="search-field">
          <Search size={16} />
          <input className="search-input" placeholder="Search resort, outlet, or cycle..." value={searchText} onChange={(event) => setSearchText(event.target.value)} />
        </label>
      </div>

      <SectionTabs
        value={tab}
        onChange={(value) => setTab(value as typeof tab)}
        options={[
          { value: "all", label: "All reviews" },
          { value: "manager", label: "Manager" },
          { value: "oan", label: "OAN" },
        ]}
      />

      <div className="metric-grid">
        <StatCard label="Visible reviews" value={filteredItems.length} />
        <StatCard label="Manager actions" value={filteredItems.filter((summary) => summary.steps.some((step) => step.stepNo === 9 && step.status !== "complete")).length} />
        <StatCard label="OAN actions" value={filteredItems.filter((summary) => summary.steps.some((step) => step.stepNo === 13 && step.status !== "complete")).length} />
        <StatCard label="Exceptions" value={filteredItems.filter((summary) => summary.computed.exceptionFlag).length} tone="danger" />
      </div>

      <article className="card stack">
        {tab === "all" ? (
          <div className="callout">
            <ShieldCheck size={18} />
            <p>Use the Manager or OAN tabs for inline approval actions. The All reviews tab is kept cleaner for scanning.</p>
          </div>
        ) : null}
        {filteredItems.length ? (
          <div className="table-shell">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Cycle</th>
                  <th>Outlet</th>
                  <th>Issue</th>
                  <th>Manager gate</th>
                  <th>OAN gate</th>
                  {inlineReviewTab ? <th>Review note</th> : null}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((summary) => {
                  const managerStep = summary.steps.find((step) => step.stepNo === 9);
                  const oanStep = summary.steps.find((step) => step.stepNo === 13);
                  const noteValue = notes[summary.stocktake.id] ?? "";
                  return (
                    <tr key={summary.stocktake.id}>
                      <td>{summary.cycle.code}</td>
                      <td>
                        <div className="summary-stack">
                          <strong>{summary.outlet.name}</strong>
                          <span className="muted">{summary.resort.name}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`pill ${getIssueClass(summary)}`}>{getIssueLabel(summary)}</span>
                      </td>
                      <td>{managerStep?.status.replaceAll("_", " ") ?? "n/a"}</td>
                      <td>{oanStep?.status.replaceAll("_", " ") ?? "n/a"}</td>
                      {inlineReviewTab ? (
                        <td>
                          <input
                            className="table-note-input"
                            placeholder="Add note..."
                            value={noteValue}
                            onChange={(event) =>
                              setNotes((current) => ({
                                ...current,
                                [summary.stocktake.id]: event.target.value,
                              }))
                            }
                          />
                        </td>
                      ) : null}
                      <td>
                        <div className="table-actions">
                          <NavLink className="table-button" to={`/stocktakes/${summary.stocktake.id}`}>
                            Open
                          </NavLink>
                          {tab === "manager" && canManager ? (
                            <>
                              <button
                                className="table-button"
                                type="button"
                                onClick={() =>
                                  managerMutation.mutate({
                                    stocktakeId: summary.stocktake.id,
                                    action: "approve",
                                    reason: noteValue,
                                  })
                                }
                                disabled={managerMutation.isPending}
                              >
                                Approve
                              </button>
                              <button
                                className="table-button"
                                type="button"
                                onClick={() =>
                                  managerMutation.mutate({
                                    stocktakeId: summary.stocktake.id,
                                    action: "reject",
                                    reason: noteValue || "Please correct and resubmit.",
                                  })
                                }
                                disabled={managerMutation.isPending}
                              >
                                Reject
                              </button>
                            </>
                          ) : null}
                          {tab === "oan" && canOan ? (
                            <>
                              <button
                                className="table-button"
                                type="button"
                                onClick={() =>
                                  oanMutation.mutate({
                                    stocktakeId: summary.stocktake.id,
                                    action: "verify",
                                    notesText: noteValue,
                                  })
                                }
                                disabled={oanMutation.isPending}
                              >
                                Verify
                              </button>
                              <button
                                className="table-button"
                                type="button"
                                onClick={() =>
                                  oanMutation.mutate({
                                    stocktakeId: summary.stocktake.id,
                                    action: "return",
                                    notesText: noteValue || "Returned for correction.",
                                  })
                                }
                                disabled={oanMutation.isPending}
                              >
                                Return
                              </button>
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title="No reviews waiting" description="This queue is clear for the current filters." />
        )}
        {filteredItems.length > 40 ? (
          <div className="table-footer">
            <span className="muted">
              Showing {visibleItems.length} of {filteredItems.length} review items.
            </span>
            <button className="button button-secondary" type="button" onClick={() => setShowAll((current) => !current)}>
              {showAll ? "Show first 40" : "Show all"}
            </button>
          </div>
        ) : null}
        {managerMutation.error ? <p className="error-text">{managerMutation.error.message}</p> : null}
        {oanMutation.error ? <p className="error-text">{oanMutation.error.message}</p> : null}
      </article>
    </section>
  );
}

function PayrollPage({ user }: { user: AuthUser }) {
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [selectedManifest, setSelectedManifest] = useState<unknown | null>(null);
  const payrollQuery = useQuery({
    queryKey: ["payroll"],
    queryFn: api.payroll,
  });

  const processMutation = useMutation({
    mutationFn: api.processPayroll,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["payroll"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
        queryClient.invalidateQueries({ queryKey: ["stocktake"] }),
        queryClient.invalidateQueries({ queryKey: ["reviews"] }),
      ]);
    },
  });
  const manifestMutation = useMutation({
    mutationFn: api.archive,
    onSuccess: (manifest) => setSelectedManifest(manifest),
  });

  if (payrollQuery.isLoading) {
    return <LoadingScreen label="Loading payroll queue" />;
  }

  if (payrollQuery.error || !payrollQuery.data) {
    return <ErrorPanel message={payrollQuery.error?.message ?? "Unable to load the payroll queue."} />;
  }

  const roles = entityRolesForActiveWorkspace(user);
  const canProcess = Boolean(user.globalRole && user.globalRole !== "master_readonly") || roles.includes("entity_admin") || roles.includes("payroll");
  const filteredItems = payrollQuery.data.filter((summary) => {
    const normalizedSearch = searchText.trim().toLowerCase();
    return (
      !normalizedSearch ||
      `${summary.resort.name} ${summary.outlet.name} ${summary.cycle.code}`.toLowerCase().includes(normalizedSearch)
    );
  });
  const verifiedCount = filteredItems.filter((item) => item.payrollSubmission?.status === "verified").length;
  const processedCount = filteredItems.filter((item) => item.payrollSubmission?.status === "processed").length;
  const totalDeductions = filteredItems.reduce(
    (sum, item) => sum + item.deductions.reduce((deductionSum, deduction) => deductionSum + deduction.amount, 0),
    0
  );
  const visibleItems = showAll ? filteredItems : filteredItems.slice(0, 40);

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Member workflow</p>
          <h1>Payroll queue</h1>
          <p className="page-summary">
            Review OAN-verified packs, process only what is ready, and inspect the generated archive manifest without
            leaving the queue.
          </p>
        </div>
      </header>

      <div className="filter-bar">
        <label className="search-field">
          <Search size={16} />
          <input className="search-input" placeholder="Search resort, outlet, or cycle..." value={searchText} onChange={(event) => setSearchText(event.target.value)} />
        </label>
      </div>

      <div className="metric-grid">
        <StatCard label="Queue items" value={filteredItems.length} />
        <StatCard label="Verified by OAN" value={verifiedCount} tone="warn" />
        <StatCard label="Processed" value={processedCount} tone="good" />
        <StatCard label="Total deductions" value={totalDeductions.toFixed(2)} />
      </div>

      <div className="page-grid">
        <article className="card stack">
          {filteredItems.length ? (
            <div className="table-shell">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Cycle</th>
                    <th>Outlet</th>
                    <th>Deductions</th>
                    <th>Payroll status</th>
                    <th>Ready</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleItems.map((summary) => (
                    <tr key={summary.stocktake.id}>
                      <td>{summary.cycle.code}</td>
                      <td>
                        <div className="summary-stack">
                          <strong>{summary.outlet.name}</strong>
                          <span className="muted">{summary.resort.name}</span>
                        </div>
                      </td>
                      <td>{summary.deductions.reduce((sum, deduction) => sum + deduction.amount, 0).toFixed(2)}</td>
                      <td>{summary.payrollSubmission?.status ?? "pending"}</td>
                      <td>{summary.computed.payrollReady ? "Yes" : "No"}</td>
                      <td>
                        <div className="table-actions">
                          <button
                            className="table-button"
                            type="button"
                            onClick={() => manifestMutation.mutate(summary.stocktake.id)}
                            disabled={manifestMutation.isPending}
                          >
                            View manifest
                          </button>
                          <button
                            className="table-button"
                            type="button"
                            onClick={() => processMutation.mutate(summary.stocktake.id)}
                            disabled={!canProcess || summary.payrollSubmission?.status !== "verified" || processMutation.isPending}
                          >
                            Process payroll
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState title="No payroll items" description="Payroll-ready or processed stocktakes will appear here." />
          )}
          {filteredItems.length > 40 ? (
            <div className="table-footer">
              <span className="muted">
                Showing {visibleItems.length} of {filteredItems.length} payroll items.
              </span>
              <button className="button button-secondary" type="button" onClick={() => setShowAll((current) => !current)}>
                {showAll ? "Show first 40" : "Show all"}
              </button>
            </div>
          ) : null}
          {processMutation.error ? <p className="error-text">{processMutation.error.message}</p> : null}
          {manifestMutation.error ? <p className="error-text">{manifestMutation.error.message}</p> : null}
        </article>

        <article className="card stack">
          <div className="section-title">
            <div>
              <h2>Archive manifest</h2>
              <p className="muted">Use this to inspect the pack order and stored evidence metadata.</p>
            </div>
            <Archive size={18} />
          </div>
          {selectedManifest ? (
            <pre className="manifest-pre">{JSON.stringify(selectedManifest, null, 2)}</pre>
          ) : (
            <EmptyState title="No manifest selected" description="Choose View manifest for a payroll item to inspect the generated pack summary." />
          )}
        </article>
      </div>
    </section>
  );
}

function EntitySetupPage({ user }: { user: AuthUser }) {
  const queryClient = useQueryClient();
  const entityId = user.activeWorkspace.entityId;
  const [tab, setTab] = useState<SetupTab>("overview");
  const [structureSearch, setStructureSearch] = useState("");
  const [documentsDraft, setDocumentsDraft] = useState<RequiredDocumentTemplateRecord[]>([]);
  const [countersDraft, setCountersDraft] = useState<CounterProfileRecord[]>([]);
  const [scopeType, setScopeType] = useState<"entity" | "resort" | "outlet">("entity");
  const [scopeId, setScopeId] = useState("");
  const [role, setRole] = useState<EntityRole>("entity_admin");
  const [assignments, setAssignments] = useState<
    Array<{
      role: EntityRole;
      scopeType: "entity" | "resort" | "outlet";
      scopeId?: string | null;
    }>
  >([]);

  const setupQuery = useQuery({
    queryKey: ["entity", "setup", entityId],
    queryFn: () => api.entitySetup(entityId ?? ""),
    enabled: Boolean(entityId),
  });

  const refreshSetup = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["entity", "setup", entityId] }),
      queryClient.invalidateQueries({ queryKey: ["auth", "me"] }),
      queryClient.invalidateQueries({ queryKey: ["master", "entities"] }),
      queryClient.invalidateQueries({ queryKey: ["master", "overview"] }),
    ]);
  };

  const settingsMutation = useMutation({
    mutationFn: (body: Partial<EntitySettingsRecord>) => api.saveEntitySettings(entityId ?? "", body),
    onSuccess: refreshSetup,
  });
  const resortMutation = useMutation({
    mutationFn: (body: { code: string; name: string }) => api.saveResort(entityId ?? "", body),
    onSuccess: refreshSetup,
  });
  const outletMutation = useMutation({
    mutationFn: (body: { resortId: string; code: string; name: string; active: boolean }) => api.saveOutlet(entityId ?? "", body),
    onSuccess: refreshSetup,
  });
  const documentsMutation = useMutation({
    mutationFn: (body: RequiredDocumentTemplateRecord[]) => api.saveDocuments(entityId ?? "", body),
    onSuccess: refreshSetup,
  });
  const countersMutation = useMutation({
    mutationFn: (body: CounterProfileRecord[]) => api.saveCounters(entityId ?? "", body),
    onSuccess: refreshSetup,
  });
  const userMutation = useMutation({
    mutationFn: (body: {
      email: string;
      name: string;
      password?: string;
      globalRole?: "master_admin" | "master_ops" | "master_readonly" | null;
      isDefault: boolean;
      assignments: Array<{
        role: EntityRole;
        scopeType: "entity" | "resort" | "outlet";
        scopeId?: string | null;
      }>;
    }) => api.saveEntityUser(entityId ?? "", body),
    onSuccess: async () => {
      setAssignments([]);
      setScopeType("entity");
      setScopeId("");
      setRole("entity_admin");
      await refreshSetup();
    },
  });

  useEffect(() => {
    if (!setupQuery.data) {
      return;
    }
    setDocumentsDraft(setupQuery.data.requiredDocuments);
    setCountersDraft(setupQuery.data.counterProfiles);
  }, [setupQuery.data]);

  if (!entityId) {
    return <ErrorPanel message="No active member workspace is selected." />;
  }

  if (setupQuery.isLoading) {
    return <LoadingScreen label="Loading member setup" />;
  }

  if (setupQuery.error || !setupQuery.data) {
    return <ErrorPanel message={setupQuery.error?.message ?? "Unable to load the entity setup."} />;
  }

  const workspace = setupQuery.data;
  const canManage = Boolean(user.globalRole && user.globalRole !== "master_readonly") || entityRolesForActiveWorkspace(user).includes("entity_admin");
  const remainingDocumentTypes = documentTypes.filter(
    (documentType) => !documentsDraft.some((document) => document.documentType === documentType)
  );
  const filteredOutlets = workspace.outlets.filter((outlet) => {
    const normalizedSearch = structureSearch.trim().toLowerCase();
    return (
      !normalizedSearch ||
      `${outlet.code} ${outlet.name} ${workspace.resorts.find((resort) => resort.id === outlet.resortId)?.name ?? ""}`
        .toLowerCase()
        .includes(normalizedSearch)
    );
  });
  const scopeOptions =
    scopeType === "resort"
      ? workspace.resorts.map((resort) => ({ id: resort.id, label: resort.name }))
      : scopeType === "outlet"
      ? workspace.outlets.map((outlet) => ({
          id: outlet.id,
          label: `${workspace.resorts.find((resort) => resort.id === outlet.resortId)?.code ?? ""} - ${outlet.name}`,
        }))
      : [{ id: workspace.entity.id, label: "Whole member" }];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <p className="eyebrow">{workspace.entity.name}</p>
          <h1>Member setup</h1>
          <p className="page-summary">
            Configure the member or resort once, then let outlet teams operate in a cleaner day-to-day workflow under
            ROA review.
          </p>
        </div>
      </header>

      <div className="metric-grid">
        <StatCard label="Resorts" value={workspace.resorts.length} />
        <StatCard label="Outlets" value={workspace.outlets.length} />
        <StatCard label="Required documents" value={workspace.requiredDocuments.filter((document) => document.required).length} />
        <StatCard label="Members" value={workspace.members.length} />
      </div>

      <SectionTabs
        value={tab}
        onChange={(value) => setTab(value as SetupTab)}
        options={[
          { value: "overview", label: "Overview" },
          { value: "documents", label: "Documents" },
          { value: "structure", label: "Structure" },
          { value: "users", label: "Users" },
        ]}
      />

      {tab === "overview" ? (
        <div className="page-grid">
          <article className="card stack">
            <div className="section-title">
              <div>
                <h2>Member profile</h2>
                <p className="muted">Storage quota, support contact, notes, and backup folder defaults for this member workspace.</p>
              </div>
            </div>
            <form
              className="stack form-grid"
              onSubmit={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                settingsMutation.mutate({
                  supportEmail: String(formData.get("supportEmail") ?? "") || null,
                  storageQuotaBytes: Number(formData.get("storageQuotaBytes") ?? workspace.settings?.storageQuotaBytes ?? 0),
                  oneDriveRootFolder: String(formData.get("oneDriveRootFolder") ?? "") || null,
                  notes: String(formData.get("notes") ?? "") || null,
                });
              }}
            >
              <label className="field">
                <span>Support email</span>
                <input name="supportEmail" type="email" defaultValue={workspace.settings?.supportEmail ?? ""} disabled={!canManage || settingsMutation.isPending} />
              </label>
              <label className="field">
                <span>Storage quota bytes</span>
                <input name="storageQuotaBytes" type="number" defaultValue={workspace.settings?.storageQuotaBytes ?? 0} disabled={!canManage || settingsMutation.isPending} />
              </label>
              <label className="field">
                <span>OneDrive root folder</span>
                <input name="oneDriveRootFolder" defaultValue={workspace.settings?.oneDriveRootFolder ?? ""} disabled={!canManage || settingsMutation.isPending} />
              </label>
              <label className="field">
                <span>Notes</span>
                <textarea name="notes" rows={5} defaultValue={workspace.settings?.notes ?? ""} disabled={!canManage || settingsMutation.isPending} />
              </label>
              <button className="button button-primary" type="submit" disabled={!canManage || settingsMutation.isPending}>
                {settingsMutation.isPending ? "Saving..." : "Save settings"}
              </button>
              {settingsMutation.error ? <p className="error-text">{settingsMutation.error.message}</p> : null}
            </form>
          </article>

          <article className="card stack">
            <div className="section-title">
              <div>
                <h2>Current users</h2>
                <p className="muted">Who can access this member workspace and at what scope.</p>
              </div>
            </div>
            {workspace.members.length ? (
              <div className="summary-stack">
                {workspace.members.map((member) => (
                  <div key={member.id} className="detail-row">
                    <div className="summary-stack">
                      <strong>{member.user?.name ?? member.userId}</strong>
                      <span className="muted">{member.user?.email ?? "No email"}</span>
                    </div>
                    <div className="pill-row">
                      {member.assignments.map((assignment) => (
                        <span key={assignment.id} className="pill">
                          {humanizeRole(assignment.role)} - {humanizeScopeType(assignment.scopeType)}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState title="No users" description="Add member users from the Users tab." />
            )}
          </article>
        </div>
      ) : null}

      {tab === "documents" ? (
        <div className="page-grid">
          <article className="card stack">
            <div className="section-title">
              <div>
                <h2>Required documents</h2>
                <p className="muted">Add only the document checkpoints you want in this workflow and label them the way this member uses them.</p>
              </div>
              <button
                className="button button-secondary"
                type="button"
                disabled={!canManage || remainingDocumentTypes.length === 0}
                onClick={() =>
                  setDocumentsDraft((current) => [
                    ...current,
                    createDocumentDraft(workspace.entity.id, remainingDocumentTypes[0], current.length + 1),
                  ])
                }
              >
                Add document
              </button>
            </div>
            {documentsDraft.length ? (
              <div className="stack">
                {documentsDraft.map((document, index) => {
                  const typeChoices = [document.documentType, ...remainingDocumentTypes.filter((item) => item !== document.documentType)];
                  return (
                    <div key={document.id} className="outlet-stack">
                      <select
                        value={document.documentType}
                        onChange={(event) =>
                          setDocumentsDraft((current) =>
                            current.map((item, itemIndex) =>
                              itemIndex === index
                                ? {
                                    ...item,
                                    documentType: event.target.value as DocumentType,
                                    label:
                                      item.label === item.documentType.replaceAll("_", " ")
                                        ? event.target.value.replaceAll("_", " ")
                                        : item.label,
                                  }
                                : item
                            )
                          )
                        }
                        disabled={!canManage}
                      >
                        {typeChoices.map((choice) => (
                          <option key={choice} value={choice}>
                            {choice.replaceAll("_", " ")}
                          </option>
                        ))}
                      </select>
                      <input
                        value={document.label}
                        onChange={(event) =>
                          setDocumentsDraft((current) =>
                            current.map((item, itemIndex) => (itemIndex === index ? { ...item, label: event.target.value } : item))
                          )
                        }
                        disabled={!canManage}
                      />
                      <label className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={document.required}
                          onChange={(event) =>
                            setDocumentsDraft((current) =>
                              current.map((item, itemIndex) => (itemIndex === index ? { ...item, required: event.target.checked } : item))
                            )
                          }
                          disabled={!canManage}
                        />
                        <span>Required</span>
                      </label>
                      <button
                        className="button button-secondary"
                        type="button"
                        disabled={!canManage}
                        onClick={() => setDocumentsDraft((current) => current.filter((item) => item.id !== document.id))}
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState title="No documents yet" description="Add the first required document once you decide how this member's pack should be structured." />
            )}
            <button
              className="button button-primary"
              type="button"
              disabled={!canManage || documentsMutation.isPending}
              onClick={() => documentsMutation.mutate(documentsDraft.map((document, index) => ({ ...document, sortOrder: index + 1 })))}
            >
              {documentsMutation.isPending ? "Saving..." : "Save document template"}
            </button>
            {documentsMutation.error ? <p className="error-text">{documentsMutation.error.message}</p> : null}
          </article>

          <article className="card stack">
            <div className="section-title">
              <div>
                <h2>Counters</h2>
                <p className="muted">Add only the counting roles this member will actually use so segregation rules match the final process.</p>
              </div>
              <button
                className="button button-secondary"
                type="button"
                disabled={!canManage}
                onClick={() => setCountersDraft((current) => [...current, createCounterDraft(workspace.entity.id, current.length + 1)])}
              >
                Add counter
              </button>
            </div>
            {countersDraft.length ? (
              <div className="stack">
                {countersDraft.map((counter, index) => (
                  <div key={counter.id} className="outlet-stack">
                    <input
                      value={counter.name}
                      onChange={(event) =>
                        setCountersDraft((current) =>
                          current.map((item, itemIndex) => (itemIndex === index ? { ...item, name: event.target.value } : item))
                        )
                      }
                      disabled={!canManage}
                    />
                    <input
                      value={counter.roleLabel}
                      onChange={(event) =>
                        setCountersDraft((current) =>
                          current.map((item, itemIndex) => (itemIndex === index ? { ...item, roleLabel: event.target.value } : item))
                        )
                      }
                      disabled={!canManage}
                    />
                    <input
                      value={counter.distinctGroup}
                      onChange={(event) =>
                        setCountersDraft((current) =>
                          current.map((item, itemIndex) => (itemIndex === index ? { ...item, distinctGroup: event.target.value } : item))
                        )
                      }
                      disabled={!canManage}
                    />
                    <button
                      className="button button-secondary"
                      type="button"
                      disabled={!canManage}
                      onClick={() => setCountersDraft((current) => current.filter((item) => item.id !== counter.id))}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState title="No counters yet" description="Add the counting roles you want to enforce before outlet teams start working in this member workspace." />
            )}
            <button
              className="button button-primary"
              type="button"
              disabled={!canManage || countersMutation.isPending}
              onClick={() => countersMutation.mutate(countersDraft.map((counter, index) => ({ ...counter, sortOrder: index + 1 })))}
            >
              {countersMutation.isPending ? "Saving..." : "Save counters"}
            </button>
            {countersMutation.error ? <p className="error-text">{countersMutation.error.message}</p> : null}
          </article>
        </div>
      ) : null}

      {tab === "structure" ? (
        <div className="page-grid">
          <article className="card stack">
            <div className="section-title">
              <div>
                <h2>Resorts and outlets / stores</h2>
                <p className="muted">Set up the resort and outlet/store structure for this member, then assign people and workflow scope to it.</p>
              </div>
            </div>

            <form
              className="inline-form"
              onSubmit={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                resortMutation.mutate({
                  code: String(formData.get("code") ?? ""),
                  name: String(formData.get("name") ?? ""),
                });
                event.currentTarget.reset();
              }}
            >
              <input name="code" placeholder="Resort code" disabled={!canManage || resortMutation.isPending} />
              <input name="name" placeholder="Resort name" disabled={!canManage || resortMutation.isPending} />
              <button className="button button-secondary" type="submit" disabled={!canManage || resortMutation.isPending}>
                Add resort
              </button>
            </form>

            <form
              className="inline-form"
              onSubmit={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                outletMutation.mutate({
                  resortId: String(formData.get("resortId") ?? ""),
                  code: String(formData.get("code") ?? ""),
                  name: String(formData.get("name") ?? ""),
                  active: formData.get("active") === "on",
                });
                event.currentTarget.reset();
              }}
            >
              <select name="resortId" disabled={!canManage || outletMutation.isPending} defaultValue={workspace.resorts[0]?.id ?? ""}>
                {workspace.resorts.map((resort) => (
                  <option key={resort.id} value={resort.id}>
                    {resort.name}
                  </option>
                ))}
              </select>
              <input name="code" placeholder="Outlet code" disabled={!canManage || outletMutation.isPending} />
              <input name="name" placeholder="Outlet name" disabled={!canManage || outletMutation.isPending} />
              <label className="checkbox-row">
                <input type="checkbox" name="active" defaultChecked />
                <span>Active</span>
              </label>
              <button className="button button-secondary" type="submit" disabled={!canManage || outletMutation.isPending}>
                Add outlet
              </button>
            </form>

            <label className="search-field">
              <Search size={16} />
              <input className="search-input" placeholder="Search resorts or outlets" value={structureSearch} onChange={(event) => setStructureSearch(event.target.value)} />
            </label>

            <div className="table-shell">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Resort</th>
                    <th>Code</th>
                    <th>Outlet count</th>
                  </tr>
                </thead>
                <tbody>
                  {workspace.resorts.map((resort) => (
                    <tr key={resort.id}>
                      <td>{resort.name}</td>
                      <td>{resort.code}</td>
                      <td>{workspace.outlets.filter((outlet) => outlet.resortId === resort.id).length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="table-shell">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Outlet</th>
                    <th>Code</th>
                    <th>Resort</th>
                    <th>Active</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOutlets.slice(0, 40).map((outlet) => (
                    <tr key={outlet.id}>
                      <td>{outlet.name}</td>
                      <td>{outlet.code}</td>
                      <td>{workspace.resorts.find((resort) => resort.id === outlet.resortId)?.name ?? "Unknown"}</td>
                      <td>{outlet.active ? "Yes" : "No"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredOutlets.length > 40 ? <p className="muted">Showing the first 40 matching outlets.</p> : null}
            {resortMutation.error ? <p className="error-text">{resortMutation.error.message}</p> : null}
            {outletMutation.error ? <p className="error-text">{outletMutation.error.message}</p> : null}
          </article>
        </div>
      ) : null}

      {tab === "users" ? (
        <div className="page-grid">
          <article className="card stack">
            <div className="section-title">
              <div>
              <h2>Add user</h2>
              <p className="muted">Build ROA-reviewed member access with a proper scope selector instead of typing raw IDs.</p>
            </div>
          </div>

            <div className="filter-bar">
              <label className="field compact-field">
                <span>Role</span>
                <select value={role} onChange={(event) => setRole(event.target.value as EntityRole)} disabled={!canManage}>
                  {entityRoleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field compact-field">
                <span>Scope type</span>
                <select
                  value={scopeType}
                  onChange={(event) => {
                    setScopeType(event.target.value as "entity" | "resort" | "outlet");
                    setScopeId("");
                  }}
                  disabled={!canManage}
                >
                  <option value="entity">Member</option>
                  <option value="resort">Resort</option>
                  <option value="outlet">Outlet / Store</option>
                </select>
              </label>
              <label className="field compact-field">
                <span>Scope</span>
                <select value={scopeId} onChange={(event) => setScopeId(event.target.value)} disabled={!canManage}>
                  <option value="">Select scope</option>
                  {scopeOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <button
                className="button button-secondary"
                type="button"
                disabled={!canManage || (scopeType !== "entity" && !scopeId)}
                onClick={() =>
                  setAssignments((current) => [
                    ...current,
                    {
                      role,
                      scopeType,
                      scopeId: scopeType === "entity" ? null : scopeId,
                    },
                  ])
                }
              >
                Add assignment
              </button>
            </div>

            {assignments.length ? (
              <div className="pill-row">
                {assignments.map((assignment, index) => (
                  <button
                    key={`${assignment.role}-${assignment.scopeType}-${assignment.scopeId ?? "entity"}-${index}`}
                    type="button"
                    className="pill"
                    onClick={() => setAssignments((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                  >
                    {humanizeRole(assignment.role)} - {describeAssignmentScope(
                      assignment.scopeType,
                      scopeOptions.find((option) => option.id === assignment.scopeId)?.label ?? assignment.scopeId
                    )}
                  </button>
                ))}
              </div>
            ) : null}

            <form
              className="stack"
              onSubmit={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                userMutation.mutate({
                  email: String(formData.get("email") ?? ""),
                  name: String(formData.get("name") ?? ""),
                  password: String(formData.get("password") ?? ""),
                  isDefault: formData.get("isDefault") === "on",
                  assignments,
                });
                event.currentTarget.reset();
              }}
            >
              <label className="field">
                <span>Name</span>
                <input name="name" disabled={!canManage || userMutation.isPending} />
              </label>
              <label className="field">
                <span>Email</span>
                <input name="email" type="email" autoComplete="email" disabled={!canManage || userMutation.isPending} />
              </label>
              <label className="field">
                <span>Launch password</span>
                <input name="password" type="password" autoComplete="new-password" disabled={!canManage || userMutation.isPending} />
              </label>
              <label className="checkbox-row">
                <input type="checkbox" name="isDefault" defaultChecked />
                <span>Default member workspace for this user</span>
              </label>
              <button className="button button-primary" type="submit" disabled={!canManage || userMutation.isPending || assignments.length === 0}>
                {userMutation.isPending ? "Saving..." : "Create user"}
              </button>
              {userMutation.error ? <p className="error-text">{userMutation.error.message}</p> : null}
            </form>
          </article>

          <article className="card stack">
            <div className="section-title">
              <div>
                <h2>User access</h2>
                <p className="muted">Current member workspace access, grouped by person.</p>
              </div>
            </div>
            {workspace.members.length ? (
              <div className="table-shell">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Assignments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workspace.members.map((member) => (
                      <tr key={member.id}>
                        <td>{member.user?.name ?? member.userId}</td>
                        <td>{member.user?.email ?? "No email"}</td>
                        <td>
                          <div className="pill-row">
                            {member.assignments.map((assignment) => (
                              <span key={assignment.id} className="pill">
                                {humanizeRole(assignment.role)} - {humanizeScopeType(assignment.scopeType)}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState title="No users yet" description="Create a user with one or more assignments from the form on the left." />
            )}
          </article>
        </div>
      ) : null}
    </section>
  );
}

export default App;
